import React, { useState, useEffect } from "react";
import { Bet } from "@/api/betting";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle } from "lucide-react";

interface UpdateResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (resultText: string) => void;
  status: "won" | "lost";
  bet: Bet;
}

const UpdateResultModal: React.FC<UpdateResultModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  status,
  bet,
}) => {
  const [resultText, setResultText] = useState("");

  // Extract validation data if available
  const outcomeData = bet.validation_result?.outcome;
  const suggestedScore = outcomeData?.score;

  const title = status === "won" ? "Mark Bet as Won" : "Mark Bet as Lost";
  const iconColor = status === "won" ? "text-green-500" : "text-red-500";
  const icon =
    status === "won" ? (
      <CheckCircle className="h-5 w-5" />
    ) : (
      <XCircle className="h-5 w-5" />
    );
  const buttonBg =
    status === "won"
      ? "bg-green-600 hover:bg-green-700"
      : "bg-red-600 hover:bg-red-700";

  // Reset result text when the bet changes
  useEffect(() => {
    // If we have a validated outcome with a score, use it as a suggestion
    if (suggestedScore) {
      setResultText(suggestedScore);
    } else {
      setResultText("");
    }
  }, [bet.id, suggestedScore]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={iconColor}>{icon}</span>
            {title}
          </DialogTitle>
          <DialogDescription>
            Enter the actual result details for this bet.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Game/Match Info */}
          <div className="text-sm">
            <div className="font-medium mb-1">{bet.game}</div>
            <div className="text-muted-foreground">{bet.prediction}</div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="result">Result Details</Label>
            <Textarea
              id="result"
              value={resultText}
              onChange={(e) => setResultText(e.target.value)}
              placeholder={
                status === "won"
                  ? "e.g., Final score: Team A 105 - Team B 98. The over hit by 30 points."
                  : "e.g., Final score: Team A 95 - Team B 98. Team A failed to cover the spread."
              }
              className="min-h-24"
            />
            {suggestedScore && (
              <div className="text-xs text-muted-foreground">
                Suggested from validation: {suggestedScore}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSubmit(resultText)}
            className={buttonBg}
          >
            Save Result
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateResultModal;
